import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import '../styles/fonts.css';

const Stockpile = () => {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const [activeSection, setActiveSection] = useState(0);
    const [overlay, setOverlay] = useState({ visible: false, text: '' });

    // Isometric Parallax
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseX = useSpring(x, { stiffness: 40, damping: 20 });
    const mouseY = useSpring(y, { stiffness: 40, damping: 20 });

    // Rotate slightly based on mouse, but keep base isometric tilt
    const rotateX = useTransform(mouseY, [-0.5, 0.5], [55, 65]); // Base 60deg tilt
    const rotateZ = useTransform(mouseX, [-0.5, 0.5], [-40, -50]); // Base -45deg rotation

    const handleMouseMove = (e) => {
        const { clientX, clientY } = e;
        const width = window.innerWidth;
        const height = window.innerHeight;
        x.set((clientX / width) - 0.5);
        y.set((clientY / height) - 0.5);
    };

    const sections = [
        {
            id: 0, label: 'Inventory Display', time: 3, icon: (
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM7.5 7.5L12 10l4.5-2.5m-9 5L12 15l4.5-2.5" />
            )
        },
        {
            id: 1, label: 'Worker Register', time: 69, icon: (
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
            )
        },
        {
            id: 2, label: 'Report Record', time: 99, icon: (
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2L14 8 20 8 M16 13L8 13 M16 17L8 17 M10 9L8 9" />
            )
        },
        {
            id: 3, label: 'PPE Issuance', time: 134, icon: (
                <path d="M20 7h-9 M14 17H5 M5 7l5 5 5-5 M14 17l-5-5-5 5" /> // Simple exchange/arrow icon
            )
        }
    ];

    const seekTo = (time, index) => {
        if (videoRef.current) {
            // 1. Pause Video
            videoRef.current.pause();

            // 2. Show Overlay
            setOverlay({ visible: true, text: sections[index].label });
            setActiveSection(index);

            // 3. Wait, then seek and play
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.currentTime = time;
                    videoRef.current.play();
                }
                // 4. Hide Overlay
                setOverlay({ visible: false, text: '' });
            }, 1500); // 1.5s duration for the overlay
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            if (videoRef.current && !overlay.visible) {
                const t = videoRef.current.currentTime;
                if (t >= 134) setActiveSection(3);
                else if (t >= 99) setActiveSection(2);
                else if (t >= 69) setActiveSection(1);
                else setActiveSection(0);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div
            className="w-full h-screen bg-[#0a0a0a] text-white overflow-hidden relative font-sans selection:bg-orange-500 selection:text-white"
            onMouseMove={handleMouseMove}
        >

            {/* Navigation to Home */}
            {/* Navigation to Home */}
            <motion.button
                onClick={() => navigate('/')}
                className="fixed top-0 left-0 z-50 flex flex-col items-center group hidden md:flex"
                style={{ padding: '20px' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
            >
                <motion.span
                    className="text-white text-sm font-medium"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
                    whileHover={{ letterSpacing: "0.2em" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                >
                    HOME
                </motion.span>
                <motion.div
                    className="w-[1px] bg-white"
                    initial={{ height: '80px', opacity: 0.6 }}
                    whileHover={{ height: '100px', opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{ marginTop: '20px' }}
                />
            </motion.button>

            {/* Navigation to Work */}
            {/* Navigation to Work */}
            <motion.button
                onClick={() => navigate('/work')}
                className="fixed top-0 right-0 z-50 flex flex-col items-center group hidden md:flex"
                style={{ padding: '20px' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
            >
                <motion.span
                    className="text-white text-sm font-medium"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                    whileHover={{ letterSpacing: "0.2em" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                >
                    WORK
                </motion.span>
                <motion.div
                    className="w-[1px] bg-white"
                    initial={{ height: '80px', opacity: 0.6 }}
                    whileHover={{ height: '100px', opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{ marginTop: '20px' }}
                />
            </motion.button>

            {/* Background Title - Shifted Left */}
            <div className="absolute inset-0 flex items-center justify-start pl-16 md:pl-32 pointer-events-none z-0 opacity-[0.03]">
                <h1 className="text-[25vw] bonky-font text-white leading-none tracking-tighter">
                    STOCK<br />PILE
                </h1>
            </div>

            {/* Content Container */}
            <div className="relative z-10 w-full h-full flex flex-col md:flex-row px-6 md:px-0 pt-4 md:pt-0 overflow-y-auto no-scrollbar">
                {/* Mobile Top Bar - HOME + Line */}
                <div className="md:hidden flex items-center mb-12 w-full gap-4">
                    <button
                        onClick={() => navigate('/')}
                        className="text-white text-sm font-medium tracking-widest uppercase"
                    >
                        HOME
                    </button>
                    <div className="h-[1px] bg-white w-24 opacity-20"></div>
                </div>

                {/* Top/Right Side: Isometric Stack (Stacked first on mobile for visual impact) */}
                <div className="w-full md:w-1/2 h-[50vh] md:h-full relative perspective-[2000px] flex items-center justify-center md:justify-start md:pl-20 order-1 md:order-2 shrink-0">
                    <motion.div
                        className="relative w-[180px] h-[360px] sm:w-[240px] sm:h-[480px] md:w-[320px] md:h-[640px] group"
                        style={{
                            rotateX,
                            rotateZ,
                            transformStyle: "preserve-3d"
                        }}
                    >
                        {/* Stack Layer 3 (Bottom) */}
                        <motion.div
                            className="absolute inset-0 bg-neutral-800 rounded-2xl border border-white/5 shadow-2xl"
                            style={{ transform: "translateZ(-60px)" }}
                            whileHover={{
                                transform: "translateZ(-120px)",
                                transition: { duration: 0.4, ease: "easeOut" }
                            }}
                        />

                        {/* Stack Layer 2 (Middle) */}
                        <motion.div
                            className="absolute inset-0 bg-neutral-900 rounded-2xl border border-white/10 shadow-2xl"
                            style={{ transform: "translateZ(-30px)" }}
                            whileHover={{
                                transform: "translateZ(-60px)",
                                transition: { duration: 0.4, ease: "easeOut" }
                            }}
                        />

                        {/* Stack Layer 1 (Top / Video) */}
                        <motion.div
                            className="absolute inset-0 bg-black rounded-2xl overflow-hidden border border-white/20 shadow-[0_0_50px_rgba(0,0,0,0.5)] transform translate-z-0"
                            whileHover={{
                                scale: 1.05,
                                transform: "translateZ(20px)",
                                transition: { duration: 0.4, ease: "easeOut" }
                            }}
                        >
                            {/* Inner Glow */}
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none z-20 mix-blend-overlay"></div>

                            {/* Section Overlay (Scoped to Video) */}
                            <AnimatePresence>
                                {overlay.visible && (
                                    <motion.div
                                        className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 backdrop-blur-md"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <motion.h2
                                            className="text-xl md:text-3xl bonky-font text-white tracking-wider text-center px-4"
                                            initial={{ y: 10, opacity: 0, scale: 0.9 }}
                                            animate={{ y: 0, opacity: 1, scale: 1 }}
                                            exit={{ y: -10, opacity: 0, scale: 1.1 }}
                                            transition={{ duration: 0.5, ease: "easeOut" }}
                                        >
                                            {overlay.text}
                                        </motion.h2>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <video
                                ref={videoRef}
                                src="/assets/stockpile-demo.mp4"
                                className="w-full h-full object-cover opacity-90"
                                loop
                                muted
                                playsInline
                                autoPlay
                            />
                        </motion.div>

                        {/* Isometric Shadow on Floor */}
                        <motion.div
                            className="absolute -bottom-32 left-1/2 w-[150%] h-32 bg-black/40 blur-2xl rounded-full -translate-x-1/2 -translate-z-[200px]"
                            style={{ rotateX: 60 }}
                        />

                    </motion.div>
                </div>

                {/* Bottom/Left Side: Controls & Info */}
                <div className="w-full md:w-1/2 h-fit md:h-full flex flex-col justify-center items-center md:items-start md:pl-32 relative order-2 md:order-1 py-12 md:py-0 shrink-0">
                    {/* Floating Glyphs Navigation */}
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-4 md:gap-8 cursor-pointer">
                        {sections.map((section, idx) => (
                            <motion.button
                                key={section.id}
                                onClick={() => seekTo(section.time, idx)}
                                className={`group flex items-center gap-3 md:gap-6 flex-shrink-0 ${activeSection === idx ? 'opacity-100' : 'opacity-30 hover:opacity-80'} transition-all duration-300`}
                                whileHover={{ x: 10 }}
                            >
                                <div className={`w-8 h-8 md:w-12 md:h-12 rounded-lg border border-white/10 flex items-center justify-center backdrop-blur-sm transition-colors duration-300 ${activeSection === idx ? 'bg-orange-500 text-black border-orange-500' : 'bg-white/5 text-white'}`}>
                                    <svg viewBox="0 0 24 24" className="w-4 h-4 md:w-6 md:h-6 fill-none stroke-current stroke-1.5">
                                        {section.icon}
                                    </svg>
                                </div>
                                <div className="flex flex-col items-start text-left">
                                    <span className="text-white text-[10px] md:text-sm tracking-widest font-bold uppercase whitespace-nowrap">
                                        {section.label}
                                    </span>
                                </div>
                            </motion.button>
                        ))}
                    </div>

                    {/* Mobile Footer Info */}
                </div>

            </div>

            {/* Footer Details */}
            <div className="absolute bottom-8 left-8 z-20 hidden md:block">
                <h2 className="text-2xl text-white bonky-font mb-2 opacity-60">Stockpile Asset Management</h2>
                <div className="flex gap-4 text-[10px] tracking-[0.2em] text-neutral-500 uppercase">
                    <span>Isometric View</span>
                    <span>•</span>
                    <span>2025</span>
                </div>
            </div>

        </div>
    );
};

export default Stockpile;
